<template>
  <van-popup
    class="popup-box"
    v-model:show="isShow"
    position="bottom"
    :lazy-render="false"
    @click-overlay="handleClose"
  >
    <div class="pay-box">
      <div class="header-box">
        <span>{{ $t('recharge') }}</span>
        <img
          src="~/assets/images/common/icon-common-close.svg"
          alt=""
          @click="handleClose"
        />
      </div>
      <!-- 支付方式列表：点击直接支付，无需 Pay Now 按钮 -->
      <div class="pay_methods">
        <!-- 统一加载遮罩：渠道列表或前置组件 SDK 初始化中 -->
        <div
          v-if="(data.isLoading && data.paymentOptions.length) || dropIn.loading"
          class="loading-overlay"
        >
          <van-loading size="24px" color="#FF1979">{{ $t('loading') }}</van-loading>
        </div>
        <template v-if="!dropIn.loading" v-for="item in displayPaymentOptions" :key="item.channel_code">
          <!-- Google/Apple：SDK 原生按钮（前置组件，就绪后自动渲染原生按钮，未就绪降级跳转） -->
          <PaymentButton
            v-if="getChannelType(item.channel_code) === 'googlepay' || getChannelType(item.channel_code) === 'applepay'"
            :payment-method="getChannelType(item.channel_code)"
            :sdk-state="dropInState"
            :product="props.currentProduct"
            :extra="props.extra"
            :payment-type="props.paymentType"
            :mit-management-url="cardReturnUrl"
            :return-url="cardReturnUrl"
            @fallback="handlePay(item.channel_code)"
            @payment-failed="handlePayFailed"
            @paying="dropIn.paying = $event"
          >
            <div class="pay_methods_item">
              <img :src="item.logo" :alt="item.name" />
            </div>
          </PaymentButton>

          <!-- Card：自定义入口，点击开全屏（独立 sdk-state，点击时才现建会话） -->
          <CustomPaymentButton
            v-else-if="getChannelType(item.channel_code) === 'card'"
            :sdk-key="'payermaxCard'"
            :sdk-state="cardSdkState"
            @open-fullscreen="openCardFullScreen(item.channel_code)"
            @fallback="redirectPay(item.channel_code)"
          >
            <div class="pay_methods_item">
              <img :src="item.logo" :alt="item.name" />
            </div>
          </CustomPaymentButton>

          <!-- PayPal 及其他：维持原有跳转 -->
          <div
            v-else
            class="pay_methods_item"
            @click="handlePay(item.channel_code)"
          >
            <img :src="item.logo" :alt="item.name" />
          </div>
        </template>
        <!-- 更多按钮：展开PayPal（PayPal 已下线展示，加 false 隐藏；要加回来去掉 false &&） -->
        <div
          v-if="false && hasPayPal && !isExpandPayPal"
          class="more-btn"
          @click="isExpandPayPal = true"
        >
        <!-- {{ $t('other.view_more') || 'More' }} -->
           <van-icon name="arrow-down" />
        </div>
      </div>
    </div>
  </van-popup>

  <!-- PayerMax 信用卡前置组件全屏 -->
  <Teleport to="body">
    <PayermaxCardFullScreen
      v-if="dropIn.showCardFullScreen"
      :session-data="dropIn.cardSession"
      :product="props.currentProduct"
      :extra="props.extra"
      :payment-type="props.paymentType"
      :mit-management-url="cardReturnUrl"
      :return-url="cardReturnUrl"
      @success="handleCardSuccess"
      @failed="handlePayFailed"
      @close="handleCardClose"
      @render-failed="handleCardRenderFailed"
    />
  </Teleport>

  <!-- 统一全屏 loading 遮罩：PayPal/降级跳转(loading)、Card 建会话(dropIn.cardLoading) 与 Google/Apple 授权后下单跳转(dropIn.paying) 共用 -->
  <Teleport to="body">
    <div v-if="loading || dropIn.cardLoading || dropIn.paying" class="pay-fullscreen-loading">
      <van-loading size="32px" color="#FF1979" vertical>Loading...</van-loading>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {reactive, ref, computed, defineEmits, nextTick} from "vue"
import {showToast} from 'vant'
import eventBus from '../../utils/eventBus'
import PaymentButton from '~/components/payment/PaymentButton.vue'
import CustomPaymentButton from '~/components/payment/CustomPaymentButton.vue'
import PayermaxCardFullScreen from '~/components/payment/fullscreen/PayermaxCardFullScreen.vue'

const { startPay, loading, createBusinessOrder: createOrder, buildCallbackUrls } = usePay()
const { loadPayermaxSdk, getCachedSession, preloadDropIn, getPaymentOptions: fetchPaymentOptions } = usePaymentPreload()

// 前置组件相关状态
const dropIn = reactive({
  loading: false,         // SDK + 会话初始化中
  sdkReady: false,        // SDK 是否加载完成
  session: null as any,   // Google/Apple 共用的会话数据（预热缓存）
  cardSession: null as any, // 信用卡独立会话（每次打开全屏现请求，不与 Google/Apple 共用、不走缓存）
  cardLoading: false,     // 信用卡会话请求中
  showCardFullScreen: false,  // 信用卡全屏是否显示
  paying: false           // Google/Apple 原生弹窗授权通过后、下单+跳转期间（全屏 loading，避免界面无反馈）
})
const emit = defineEmits(['close', 'jumpPage'])
const props = defineProps({
  currentProduct: {
    type: Object,
    default: () => ({})
  },
  // 有效值：order=一次性支付，subscription=订阅
  paymentType: {
    type: String,
    default: 'order'
  },
  extra: {
    type: Object,
    default: () => ({})
  }
})
const data = reactive({
  payMethod: 'paypal',
  isCreating: false,
  paymentOptions: [],
  isLoading: false
})
const isShow = ref(false)
const isExpandPayPal = ref(false)

// 前置组件回调地址（复用 usePay 的回调地址构造，带 id/chapter_id/guest_id），支付成功后跳回页面复用现有轮询逻辑
const cardReturnUrl = computed(() => {
  if (typeof window === 'undefined') return ''
  return buildCallbackUrls(props.extra).return_url
})

// 获取设备类型
const getDeviceType = () => {
  if (typeof window === 'undefined') return 'other'
  const UA = navigator.userAgent.toLowerCase()

  // iOS (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/i.test(UA)) {
    return 'ios'
  }
  // Mac OS
  if (/mac/i.test(UA) && !/iphone|ipad|ipod/i.test(UA)) {
    return 'mac'
  }
  // Android
  if (/android/i.test(UA)) {
    return 'android'
  }
  // Windows
  if (/win/i.test(UA)) {
    return 'windows'
  }
  return 'other'
}

// 排序后的支付方式
const sortedPaymentOptions = computed(() => {
  if (!data.paymentOptions.length) return []

  const deviceType = getDeviceType()
  const options = [...data.paymentOptions]

  // 定义排序优先级
  const getPriority = (channelCode: string) => {
    const code = channelCode.toLowerCase()

    if (deviceType === 'ios' || deviceType === 'mac') {
      // iOS/Mac: Applepay > Card > PayPal
      if (code.includes('apple')) return 1
      if (code.includes('card')) return 2
      if (code.includes('paypal')) return 3
    } else {
      // Android/Windows/其他: Googlepay > Card > PayPal
      if (code.includes('google')) return 1
      if (code.includes('card')) return 2
      if (code.includes('paypal')) return 3
    }
    return 99
  }

  return options.sort((a, b) => getPriority(a.channel_code) - getPriority(b.channel_code))
})

// 显示的支付方式（根据展开状态）
const displayPaymentOptions = computed(() => {
  // PayPal 已下线展示：暂时注释展开分支（要加回来去掉注释即可）
  // if (isExpandPayPal.value) {
  //   return sortedPaymentOptions.value
  // }
  // 默认不显示PayPal
  return sortedPaymentOptions.value.filter(item => !item.channel_code.toLowerCase().includes('paypal'))
})

// 检查是否有PayPal选项
const hasPayPal = computed(() => {
  return data.paymentOptions.some(item => item.channel_code.toLowerCase().includes('paypal'))
})

// 渠道类型分类：决定列表项用哪个组件渲染
// google/apple → PaymentButton（SDK 原生按钮）；card → CustomPaymentButton（点击开全屏）；paypal/其他 → 旧跳转列表项
const getChannelType = (channelCode: string) => {
  const code = channelCode.toLowerCase()
  if (code.includes('google')) return 'googlepay'
  if (code.includes('apple')) return 'applepay'
  if (code.includes('card')) return 'card'
  return 'redirect' // paypal 及其他维持跳转
}

// 前置组件会话/SDK 状态（供子组件判断是否可用）
// loading 恒 false：加载态由 pay_methods 外层统一遮罩处理，避免子组件各自转圈
const dropInState = computed(() => ({
  loading: false,
  loaded: dropIn.sdkReady && !!dropIn.session,
  error: null
}))

// 信用卡独立 SDK 状态：Card 不依赖 Google/Apple 共用 session，点击时才现建会话
// loaded 恒 true → 点击总能进 openCardFullScreen（内部建会话/失败再降级）；loading 反映会话请求中
const cardSdkState = computed(() => ({
  loading: dropIn.cardLoading,
  loaded: true,
  error: null
}))

// 点击支付方式（旧列表项 / PayPal）→ 走原有跳转
const handlePay = (channelCode: string) => {
  redirectPay(channelCode)
}

// 降级 / PayPal：走原有跳转支付
const redirectPay = (channelCode: string) => {
  if (loading.value) return
  data.payMethod = channelCode
  createBusinessOrder()
}

// 信用卡前置组件：打开全屏 SDK 组件
// Card 与 Google/Apple 共用全局缓存 session（60 分钟复用一份）：命中缓存直接用，未命中才现建并写入缓存
const openCardFullScreen = async (channelCode: string) => {
  if (loading.value || dropIn.cardLoading) return
  data.payMethod = channelCode
  const rsp_id = props.currentProduct?.rsp_id
  if (!rsp_id) {
    redirectPay(channelCode) // 无 rsp_id 无法建会话 → 降级跳收银台
    return
  }
  dropIn.cardLoading = true
  try {
    const sdkLoaded = await loadPayermaxSdk()
    // 优先用对应支付类型的缓存；未命中则现建 session（写入对应桶），再取缓存
    let session = getCachedSession(props.paymentType)
    if (!session) {
      await preloadDropIn(rsp_id, props.extra, props.paymentType)
      session = getCachedSession(props.paymentType)
    }
    if (!sdkLoaded || !session) {
      redirectPay(channelCode) // 会话/ SDK 失败 → 降级跳收银台
      return
    }
    dropIn.cardSession = session
    dropIn.showCardFullScreen = true
  } catch (e) {
    console.error('信用卡会话创建失败，降级跳转:', e)
    redirectPay(channelCode)
  } finally {
    dropIn.cardLoading = false
  }
}

// 信用卡全屏支付成功
const handleCardSuccess = () => {
  dropIn.showCardFullScreen = false
  dropIn.cardSession = null
  eventBus.emit('savePageData')
  isShow.value = false
}

// 前置组件支付失败（Card 全屏 / Google / Apple 共用）：关全屏 + 提示，停在弹窗可重试
const handlePayFailed = (msg: string) => {
  dropIn.showCardFullScreen = false
  dropIn.cardSession = null // 清掉，下次打开重新请求
  if (msg) showToast(msg)
}

// 信用卡全屏关闭
const handleCardClose = () => {
  dropIn.showCardFullScreen = false
  dropIn.cardSession = null // 清掉，下次打开重新请求
}

// 信用卡全屏 SDK 渲染失败 → 关全屏，降级走原有跳转收银台
const handleCardRenderFailed = () => {
  dropIn.showCardFullScreen = false
  dropIn.cardSession = null
  redirectPay(data.payMethod)
}

const handleClose = () => {
  isShow.value = false
}

const open = async () => {
  isShow.value = true
  isExpandPayPal.value = false // 重置展开状态
  getPaymentOptions()
  // 等待 props.currentProduct 更新到位（PopupHandle 是先赋值再同步调 open）
  await nextTick()
  // 加载充值商品信息后即初始化前置组件（SDK + 会话）
  initDropIn()
}

// 预渲染（对齐 master preRender）：弹窗未打开时就填充渠道 + 用默认商品建 session 并把 SDK 按钮挂到常驻弹窗 DOM 里
// 由 popupHandle 先给 props.currentProduct/paymentType 赋值后经 ref 调用（不走事件），随后打开弹窗按钮已就绪、无 loading、不闪
const preRender = async () => {
  if (!props.currentProduct?.rsp_id) return
  await getPaymentOptions()  // 填充渠道列表，让 PaymentButton 渲染
  await initDropIn()         // 用默认商品 mount 按钮（isShow 保持 false）
}

// 初始化 PayerMax 前置组件：优先用预热缓存，未命中则现建（现建也会写入缓存，下次复用）
// 并发去重：预热 / getOrderStatus / 用户点击都可能触发 initDropIn，同一 rsp_id 进行中的任务复用，
// 避免 loading/session 反复翻转导致 PaymentButton 被 v-if 卸载重建、SDK 重复加载
let initDropInTask: Promise<void> | null = null
let initDropInRspId = ''
// 上次建成 session 的支付类型：用于区分「切类型」与「同类型 session 过期重建」，两者未命中缓存时处理不同
let lastInitType = ''
const initDropIn = (): Promise<void> => {
  const rsp_id = props.currentProduct?.rsp_id
  const paymentType = props.paymentType  // 分桶依据（order/subscription），与 currentProduct 同源
  if (!rsp_id) {
    dropIn.sdkReady = false
    dropIn.session = null
    return Promise.resolve()
  }
  // 同一 rsp_id 已在初始化中 → 复用进行中的任务（不同 rsp_id 才另起，对应切商品）
  if (initDropInTask && initDropInRspId === rsp_id) {
    return initDropInTask
  }
  initDropInRspId = rsp_id
  initDropInTask = (async () => {
    // 命中预热缓存 → 直接用（确保 SDK 脚本已加载即可），无 loading
    // 注意：命中时不先清空 sdkReady/session，避免容器 DOM 闪销毁导致按钮丢失
    const cached = getCachedSession(paymentType)
    if (cached) {
      const sdkLoaded = await loadPayermaxSdk()
      if (sdkLoaded) {
        dropIn.session = cached
        dropIn.sdkReady = true
        lastInitType = paymentType
        return
      }
    }
    // 切类型且另一类型已就绪 → 不清 session/不翻 loading：否则 v-if="!dropIn.loading" 会把整组支付方式
    // （含无关的 PayPal/Card 入口）连同已建好的按钮一起卸载重建，切换期间近 1 秒无任何支付入口可点。
    // 此时缺失桶由 PaymentButton 的 ensureBucketBuilt 就地懒建（与下方 preloadDropIn 共享 in-flight 去重，仍只 1 次请求）。
    // 同类型未命中（session 60 分钟过期）仍走原路径整组重建，避免按钮继续挂着过期 session 直到点击才报 TOKEN_ERROR
    const switchType = dropIn.sdkReady && !!dropIn.session && !!lastInitType && paymentType !== lastInitType
    if (!switchType) {
      dropIn.sdkReady = false
      dropIn.session = null
      dropIn.loading = true
    }
    try {
      // 现建：preloadDropIn 内部会创建 session（带下单参数 extra）并写入对应支付类型桶，再取缓存复用
      await preloadDropIn(rsp_id, props.extra, paymentType)
      const session = getCachedSession(paymentType)
      if (session) {
        dropIn.session = session
        dropIn.sdkReady = true
        lastInitType = paymentType
      }
    } catch (e) {
      console.error('前置组件初始化失败，将降级为跳转模式:', e)
    } finally {
      if (!switchType) dropIn.loading = false
      if (initDropInRspId === rsp_id) initDropInTask = null
    }
  })()
  return initDropInTask
}
const createBusinessOrder = async () => {
  console.log('看看选择的支付方式对不对', data.payMethod)
  // 把当前的商品信息保存起来
  localStorage.setItem('currentProduct', JSON.stringify(props.currentProduct))
  try {
    const res = await startPay(
      props.currentProduct,
      props.paymentType,
      props.extra,
      data.payMethod
    )
    console.log('支付res', res)
    if (res.code === 200) {
      //  code等于200说明能够跳转，此时通知阅读器保存一下当前的页面信息
      eventBus.emit('savePageData')
      isShow.value = false
    }
  } catch (e) {
    console.log(e)
  }
}

// 获取支付渠道（带缓存：首次请求后一直复用，避免每次打开都请求）
const getPaymentOptions = async () => {
  data.isLoading = true
  try {
    const res = await fetchPaymentOptions()
    console.log(res);
    
    if (res && res.length) {
      data.paymentOptions = res

      // 设置默认支付方式
      const defaultPayMethod = res.find((item) => item.is_default)
      let selectedMethod = defaultPayMethod ? defaultPayMethod.channel_code : res[0].channel_code

      // 如果默认是PayPal但当前没有展开PayPal，选择第一个非PayPal的方式
      if (!isExpandPayPal.value && selectedMethod.toLowerCase().includes('paypal')) {
        const firstNonPayPal = res.find(item => !item.channel_code.toLowerCase().includes('paypal'))
        if (firstNonPayPal) {
          selectedMethod = firstNonPayPal.channel_code
        }
      }

      data.payMethod = selectedMethod
    } else {
      console.error('获取支付方式失败')
    }
  } catch (e) {
    console.error('获取支付方式失败:', e)
  } finally {
    data.isLoading = false
  }
}

defineExpose({
  open,
  preRender
})
</script>

<style lang="scss">
.popup-box {
  border-radius: 22px 22px 0 0;
}
</style>
<style scoped lang="scss">
// 统一全屏 loading 遮罩（Teleport 到 body）：PayPal/降级跳转 + Card 建会话共用
.pay-fullscreen-loading {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.pay-box {
  width: 100%;
  //min-height: 300px;
  position: relative;

  .header-box {
    width: 100%;
    aspect-ratio: 750 / 120; /* 高度跟随背景图 750×120 等比自适应，不固定像素 */
    text-align: center;
    font-weight: 600;
    font-size: 17px;
    color: #212121;
    display: flex;
    align-items: center;
    justify-content: center;
    /* 容器与图同比例（6.25:1），contain 等比缩放即铺满：不裁切不压缩 */
    background: url('~/assets/images/reader/icon-reader-product-bg.svg') center top / contain no-repeat;

    > img {
      position: absolute;
      right: 17px;
      top: 23px;
      //transform: translateY(-50%);
      width: 18px;
      height: 18px;
    }
  }

  .pay_methods {
    width: 100%;
    min-height: 100px;
    padding: 20px;
    padding-top: 0;
    background: #ffffff;
    position: relative;

    .loading-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 120px;
      gap: 12px;
      color: #999;
      font-size: 14px;
    }

    .pay_methods_item {
      width: 100%;
      height: 55px;
      padding: 0 19px;
      background: #ffffff;
      border-radius: 12px;
      border: 1px solid #555555;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100px;
      }
    }

    .more-btn {
      width: 100%;
      padding: 12px;
      text-align: center;
      color: #666666;
      font-size: 14px;
      cursor: pointer;
    }
  }

  .pay-now-btn {
    height: 50px;
    background: linear-gradient(135deg, #ff394d 0%, #ff398f 100%);
    border-radius: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 15px;
    color: #ffffff;
    z-index: 999;
    margin: 0 21px 21px 21px;
    gap: 10px;
  }
}
</style>
